import React, { Dispatch, ReactNode, SetStateAction } from 'react';
import { Controller } from 'react-hook-form';
import moment from 'moment';
import useOrderDelivery from './orderDeliveryDialog-hook';
import { ButtonTypes, ICartItem, IAutocompleteItem } from '../../../../../../../../Interfaces/componentTypes';
import { VisitorPageContents } from '../../../../../visitorPage';
import { CartIcon, LocationIcon, PencilIcon } from '../../../../../../../../Components/IconComponents';
import AuthInput from '../../../../../../../../Components/Inputs/AuthInput/authInput';
import TelInput from '../../../../../../../../Components/Inputs/TelInput/telInput';
import CustomTimePicker from '../../../../../../../../Components/Inputs/TimePicker/timePicker';
import useLanguagesHooks from '../../../../../../../../Components/Languages/setLanguage-hooks';
import Button from '../../../../../../../../Components/Button/button';
import CustomDialog from '../../../../../../../../Components/Dialog/customDialog';
import NoteDialog from '../NoteDialog/noteDialog';
import styles from './orderDilivery.module.scss';
import ToggleSwitch from '../../../../../../../../Components/ToggleSwitch/toggleSwitch';
import { IDeliveryCity } from '../../../../../../../../Interfaces/responseTypes';

export interface IDeliveryOrderProps {
  companyId: number,
  company_name: string,
  company_logo: string,
  company_contacts?: string[],
  cart: ICartItem[],
  currencySymbol: string,
  totalPrice: number,
  packageFee: number,
  deliveryMinCost: number,
  deliveryCities?: IDeliveryCity[],
  totalItemCount: number,
  start_time?: string | null,
  end_time?: string | null,
  onOpenMainCartDialog: () => void;
  setDialog: Dispatch<SetStateAction<ReactNode>>;
  setMainCart: Dispatch<SetStateAction<[] | ICartItem[]>>;
  onCloseDialog: () => void;
  setContentType: Dispatch<SetStateAction<VisitorPageContents>>
}

const OrderDeliveryDialog: React.FC<IDeliveryOrderProps> = (props) => {
  const { totalPrice, packageFee, currencySymbol, deliveryMinCost, deliveryCities, onOpenMainCartDialog, totalItemCount, start_time, end_time } = props;
  const { t } = useLanguagesHooks();
  const {
    open,
    errors,
    control,
    dialogRef,
    newOrderLoading,
    totalDeliveryCost,
    calculatedDeliveryCost,
    onOrder,
    register,
    getValues,
    watch,
    handleNote,
    onOpenNoteDialog,
    onCloseNoteDialog,
  } = useOrderDelivery(props);
  
  const selectedDate = watch('date');

  return (
    <div ref={dialogRef} className={styles.container}>
      <div className={styles.header}>
        <LocationIcon color='var(--primary-hover)' />
        <p>
          {t('Visitor.Delivery.Header')}
        </p>
      </div>
      <div className={styles.form}>
        <AuthInput
          register={register}
          registerName='address'
          label={t('Visitor.Delivery.Address')}
          showTextError={false}
          required={true}
          error={errors.address}
        />
        <TelInput
          name='phone_number'
          control={control}
          label={t('Visitor.Delivery.Phone')}
          showTextError={false}
          style={styles.tellInput}
          error={errors.phone_number}
        />
        <div className={styles.dateTimeBox}>
          <p className={styles.label}>
            {t('Visitor.Delivery.Time')}
          </p>
          <div className={styles.dateTimeInputRow}>
            <AuthInput
              register={register}
              registerName='date'
              type='date'
              showTextError={false}
              style={styles.datePicker}
              inputBoxStyles={styles.datePickerInput}
              defaultValue={moment().format('YYYY-MM-DD')}
              minDate={moment().format('YYYY-MM-DD')}
            />
            <Controller
              name="time"
              control={control}
              render={({ field, fieldState }) => (
                <CustomTimePicker
                  value={field.value!}
                  onChange={field.onChange}
                  style={styles.timePicker}
                  showTextError={false}
                  placeholder=""
                  startTime={start_time}
                  endTime={end_time}
                  selectedDate={selectedDate}
                />
              )}
            />
          </div>
        </div>
        <div className={styles.noteContainer}>
          <div className={styles.noteLabel}>
            {t('Visitor.Delivery.Notes')}
          </div>
          <div className={styles.noteBox} onClick={onOpenNoteDialog}>
            <div className={styles.noteText}>
              <p >
                {getValues('notes') && getValues('notes')?.length! > 30
                  ? `${getValues('notes')?.slice(0, 30)}...`
                  : getValues('notes')}
              </p>
            </div>
            <PencilIcon color={`var(--input-label)`} />
          </div>
        </div>
      </div>
      <div className={styles.paymentBox}>
        <p className={styles.paymentLabel}>{t('Visitor.Delivery.Payment')}</p>
        <div className={styles.switcherBox}>
          <div className={styles.switcherRow}>
            <p>{t('Visitor.Delivery.Cash')}</p>
            <ToggleSwitch control={control} name='cash' />
          </div>
          <div className={styles.switcherRow}>
            <p>{t('Visitor.Delivery.Card')}</p>
            <ToggleSwitch control={control} name='card' />
          </div>
        </div>
      </div>
      <div className={styles.totalBox}>
        <div className={styles.totalInfoBox}>
          <div className={styles.totaInfoRow}>
            <p className={styles.totalInfoTitle}>
              {t('Visitor.Delivery.Order')}
            </p>
            <div className={styles.totalItemsInfoRow}>
              <p className={styles.totalItemsCount}>
                {`${totalItemCount} ${totalItemCount > 1 ? t('Visitor.Delivery.Items') : t('Visitor.Delivery.Item')}`}
              </p>
              <p className={styles.totalCost}>
                {`${totalPrice}${currencySymbol}`}
              </p>
            </div>
          </div>
          <div className={styles.totaInfoRow}>
            <p className={styles.totalInfoTitle}>
              {t('Visitor.Delivery.Package_Cost')}
            </p>
            <p className={styles.totalCost}>
              {`${packageFee ?? 0}${currencySymbol}`}
            </p>
          </div>
          <div className={styles.totaInfoRow}>
            <div className={styles.deliveryTitleBox}>
              <p className={styles.totalInfoTitle}>
                {t('Visitor.Delivery.Delivery_Fee')}
              </p>
              <p className={styles.deliverySubtitle}>
                {t('Visitor.Delivery.Subtitle')}
              </p>
            </div>
            <p className={styles.totalCost}>
              {`${Math.round(Number(calculatedDeliveryCost || deliveryMinCost || 0))}${currencySymbol}`}
            </p>
          </div>
        </div>
        <div className={styles.totaInfoRow}>
          <p className={styles.totalDeliveryTitle}>
            {t('Visitor.Delivery.Total')}
          </p>
          <p className={styles.totalDeliveryCost}>
            {`${Math.round(Number(totalDeliveryCost || 0))} ${currencySymbol}`}
          </p>
        </div>
      </div>
      <div className={styles.buttonsRow}>
        <Button
          buttonType={ButtonTypes.Primery}
          title={t('Visitor.Cart')}
          leftIcon={<CartIcon width={20} height={20} />}
          buttonStyle={styles.cartButton}
          buttonTitleStyle={styles.buttontitle}
          onClick={onOpenMainCartDialog}
        />
        <Button
          buttonType={ButtonTypes.Primery}
          title={t('Visitor.Delivery.Order_Button')}
          buttonStyle={styles.orderButton}
          buttonTitleStyle={styles.buttontitle}
          isLoading={newOrderLoading}
          onClick={onOrder}
        />
      </div>
      <CustomDialog
        open={open}
        onClose={onCloseNoteDialog}
        children={
          <NoteDialog
            note={getValues('notes')}
            onHandleNote={handleNote} />}
      />
    </div>
  )
}

export default OrderDeliveryDialog
